import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useAdminOrders, useKitchenStations } from '../../lib/admin-hooks';
import { Skeleton } from '../../components/ui/Skeleton';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Clock, Check, AlertCircle, CreditCard, Banknote, Smartphone, Play, ChefHat, Truck, XCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
// Global roles that can manage the full order lifecycle
const GLOBAL_ROLES = ['owner', 'admin', 'manager', 'caja'];
export function KDSPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const [searchParams] = useSearchParams();
  const lockedStation = searchParams.get('station');
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  const {
    stations,
    loading: stationsLoading
  } = useKitchenStations(restaurant?.id);
  const {
    orders,
    loading: ordersLoading,
    acceptOrder,
    startOrderPreparation,
    markOrderReady,
    deliverOrder,
    cancelOrder,
    markItemPreparing,
    markItemReady
  } = useAdminOrders(restaurant?.id);
  const [activeStation, setActiveStation] = useState<string>('all');
  const [updatingOrders, setUpdatingOrders] = useState<Set<string>>(new Set());
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());
  const userRole = tenantCtx?.role || 'owner';
  const isGlobalRole = GLOBAL_ROLES.includes(userRole);
  const isGlobalStation = (code: string) => code === 'all' || code === 'caja';
  const isLockedToProduction = lockedStation != null && !isGlobalStation(lockedStation);
  useEffect(() => {
    if (lockedStation) {
      setActiveStation(lockedStation === 'caja' ? 'all' : lockedStation);
    }
  }, [lockedStation]);
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-900 flex items-center justify-center" data-id="element-648">
        <Skeleton className="w-16 h-16 rounded-full bg-gray-800" data-id="element-649" />
      </div>;
  }
  // Show active orders (not delivered/cancelled)
  const activeOrders = orders.filter((o: any) => !['delivered', 'cancelled', 'out_for_delivery'].includes(o.status));
  const displayOrders = activeOrders.filter((order: any) => {
    if (isGlobalStation(activeStation)) return true;
    return order.items.some((item: any) => item.station_code === activeStation);
  });
  const getWaitTimeColor = (createdAt: string) => {
    const minutes = Math.floor((new Date().getTime() - new Date(createdAt).getTime()) / 60000);
    if (minutes > 20) return 'text-red-500 bg-red-50 border-red-200';
    if (minutes > 10) return 'text-amber-500 bg-amber-50 border-amber-200';
    return 'text-emerald-500 bg-emerald-50 border-emerald-200';
  };
  // ─── Order-level action handler ────────────────────────────
  const handleOrderAction = async (orderId: string, action: string) => {
    setUpdatingOrders(prev => new Set(prev).add(orderId));
    try {
      switch (action) {
        case 'accept':
          await acceptOrder(orderId);
          break;
        case 'start_preparation':
          await startOrderPreparation(orderId);
          break;
        case 'mark_ready':
          await markOrderReady(orderId);
          break;
        case 'deliver':
          await deliverOrder(orderId);
          break;
        case 'cancel':
          if (confirm('¿Estás seguro de cancelar este pedido?')) {
            await cancelOrder(orderId);
          }
          break;
      }
    } finally {
      setUpdatingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };
  // ─── Item-level action handler ─────────────────────────────
  const handleItemAction = async (itemId: string, currentStatus: string) => {
    setUpdatingItems(prev => new Set(prev).add(itemId));
    try {
      if (currentStatus === 'pending') {
        await markItemPreparing(itemId);
      } else if (currentStatus === 'preparing') {
        await markItemReady(itemId);
      }
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };
  // Header title
  let headerTitle = 'KDS — Todas las Estaciones';
  if (activeStation !== 'all') {
    if (isGlobalStation(activeStation)) {
      headerTitle = 'KDS — Caja (Vista Global)';
    } else {
      const stationName = stations.find(s => s.code === activeStation)?.name;
      if (stationName) headerTitle = `KDS — ${stationName}`;
    }
  }
  const getPaymentIcon = (method: string) => {
    switch (method) {
      case 'sinpe':
        return <Smartphone className="w-3 h-3 mr-1" data-id="element-650" />;
      case 'card':
        return <CreditCard className="w-3 h-3 mr-1" data-id="element-651" />;
      case 'cash':
        return <Banknote className="w-3 h-3 mr-1" data-id="element-652" />;
      default:
        return null;
    }
  };
  const getPaymentLabel = (method: string) => {
    switch (method) {
      case 'sinpe':
        return 'SINPE';
      case 'card':
        return 'Tarjeta';
      case 'cash':
        return 'Efectivo';
      default:
        return method;
    }
  };
  const getOrderStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <Badge variant="info" className="bg-blue-50 text-blue-700 border-blue-200" data-id="element-653">
            Nuevo
          </Badge>;
      case 'accepted':
        return <Badge variant="default" className="bg-purple-50 text-purple-700 border-purple-200" data-id="element-654">
            Aceptado
          </Badge>;
      case 'preparing':
        return <Badge variant="default" className="bg-amber-50 text-amber-700 border-amber-200" data-id="element-655">
            Preparando
          </Badge>;
      case 'ready':
        return <Badge variant="success" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-id="element-656">
            Listo
          </Badge>;
      default:
        return <Badge variant="default" data-id="element-657">{status}</Badge>;
    }
  };
  const getItemStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          label: 'Pendiente',
          color: 'bg-gray-100 text-gray-600',
          actionLabel: 'En preparación',
          actionColor: 'bg-amber-500 hover:bg-amber-600 text-white border-amber-600'
        };
      case 'preparing':
        return {
          label: 'Preparando',
          color: 'bg-amber-100 text-amber-700',
          actionLabel: 'Marcar listo',
          actionColor: 'bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-600'
        };
      case 'ready':
        return {
          label: 'Listo',
          color: 'bg-emerald-100 text-emerald-700',
          actionLabel: '',
          actionColor: ''
        };
      default:
        return {
          label: status,
          color: 'bg-gray-100 text-gray-600',
          actionLabel: '',
          actionColor: ''
        };
    }
  };
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-658">
      <div className="min-h-screen bg-gray-100 p-4 sm:p-6" data-id="element-659">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-660">
          <div data-id="element-661">
            <div className="flex items-center gap-3" data-id="element-662">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-663">
                {headerTitle}
              </h1>
              <Badge variant="default" className="bg-gray-900 text-white" data-id="element-664">
                {displayOrders.length} pedidos
              </Badge>
            </div>
            <p className="text-gray-500 mt-1" data-id="element-665">
              Gestión de pedidos en tiempo real
            </p>
          </div>

          {/* Station Tabs */}
          {!isLockedToProduction && <div className="flex overflow-x-auto hide-scrollbar gap-2 w-full sm:w-auto pb-2 sm:pb-0" data-id="element-666">
              <button onClick={() => setActiveStation('all')} className={cn('px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors', activeStation === 'all' ? 'bg-brand-charcoal text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200')} data-id="element-667">
                Todos
              </button>
              {stations.map(station => <button key={station.id} onClick={() => setActiveStation(station.code)} className={cn('px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors', activeStation === station.code ? 'bg-brand-charcoal text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200')} data-id="element-668">
                  {station.name}
                </button>)}
            </div>}
        </div>

        {/* Orders Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 xl:gap-6 items-start" data-id="element-669">
          {ordersLoading || stationsLoading ? [1, 2, 3].map(i => <Skeleton key={i} className="h-96 rounded-xl" data-id="element-670" />) : displayOrders.length > 0 ? displayOrders.map((order: any) => {
          const visibleItems = order.items.filter((item: any) => isGlobalStation(activeStation) || item.station_code === activeStation);
          const allItemsReady = visibleItems.length > 0 && visibleItems.every((i: any) => i.status === 'ready' || i.status === 'served');
          const isUpdatingOrder = updatingOrders.has(order.id);
          return <div key={order.id} className={cn('bg-white rounded-xl shadow-sm border overflow-hidden flex flex-col transition-all', allItemsReady ? 'border-emerald-300 ring-1 ring-emerald-300' : order.status === 'new' ? 'border-blue-300 ring-1 ring-blue-200' : 'border-gray-200')} data-id="element-671">
                  {/* Order Header */}
                  <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-start" data-id="element-672">
                    <div data-id="element-673">
                      <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-674">
                        <span className="text-xl font-bold text-gray-900" data-id="element-675">
                          #{order.order_number}
                        </span>
                        {getOrderStatusBadge(order.status)}
                        <Badge variant={order.delivery_type === 'dine_in' ? 'default' : 'info'} data-id="element-676">
                          {order.delivery_type === 'delivery' ? 'Delivery' : order.delivery_type === 'pickup' ? 'Llevar' : 'Mesa'}
                        </Badge>
                        <Badge variant="default" className="bg-gray-100 text-gray-600 border-gray-200 flex items-center" data-id="element-677">
                          {getPaymentIcon(order.payment_method)}
                          {getPaymentLabel(order.payment_method)}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-678">
                        <p className="text-sm text-gray-600 font-medium" data-id="element-679">
                          {order.customer_name}
                        </p>
                        {order.delivery_type === 'dine_in' && order.table_id && <span className="text-xs font-bold bg-gray-200 text-gray-700 px-2 py-0.5 rounded" data-id="element-680">
                              Mesa {order.table_id}
                            </span>}
                      </div>
                    </div>
                    <div className={cn('px-3 py-1.5 rounded-lg border font-bold flex items-center gap-1.5', getWaitTimeColor(order.created_at))} data-id="element-681">
                      <Clock className="w-4 h-4" data-id="element-682" />
                      {Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / 60000)}
                      m
                    </div>
                  </div>

                  {/* Order Notes */}
                  {order.notes && <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-start gap-2" data-id="element-683">
                      <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" data-id="element-684" />
                      <p className="text-sm text-amber-800 font-medium" data-id="element-685">
                        {order.notes}
                      </p>
                    </div>}

                  {/* Order Items */}
                  <div className="p-4 flex-1 overflow-y-auto" data-id="element-686">
                    <ul className="space-y-4" data-id="element-687">
                      {visibleItems.map((item: any) => {
                  const isItemReady = item.status === 'ready' || item.status === 'served';
                  const isItemPreparing = item.status === 'preparing';
                  const isUpdatingItem = updatingItems.has(item.id);
                  const statusInfo = getItemStatusInfo(item.status);
                  return <li key={item.id} className={cn('flex gap-3 transition-opacity', isItemReady ? 'opacity-60' : 'opacity-100')} data-id="element-688">
                            <div className={cn('w-8 h-8 rounded flex items-center justify-center font-bold shrink-0', isItemReady ? 'bg-emerald-100 text-emerald-700' : isItemPreparing ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-900')} data-id="element-689">
                              {item.quantity}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-690">
                              <div className="flex items-center gap-2 flex-wrap" data-id="element-691">
                                <p className={cn('font-bold text-lg leading-tight', isItemReady ? 'text-gray-500 line-through' : 'text-gray-900')} data-id="element-692">
                                  {item.item_name_snapshot}
                                </p>
                                {isGlobalStation(activeStation) && item.station_code && <span className="text-[10px] uppercase tracking-wider font-bold bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded" data-id="element-693">
                                      {item.station_code}
                                    </span>}
                                <span className={cn('text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded', statusInfo.color)} data-id="element-694">
                                  {statusInfo.label}
                                </span>
                              </div>

                              {item.options && item.options.length > 0 && <ul className="mt-1 space-y-0.5" data-id="element-695">
                                  {item.options.map((opt: any, idx: number) => <li key={idx} className={cn('text-sm flex items-start gap-1', isItemReady ? 'text-gray-400' : 'text-gray-600')} data-id="element-696">
                                      <span className="text-gray-400 mt-0.5" data-id="element-697">
                                        •
                                      </span>
                                      {opt.option_name}
                                    </li>)}
                                </ul>}

                              {item.notes && <p className={cn('text-sm font-medium mt-1 italic', isItemReady ? 'text-red-400' : 'text-red-600')} data-id="element-698">
                                  ** {item.notes}
                                </p>}
                            </div>

                            {/* Item Action Button */}
                            <div className="shrink-0" data-id="element-699">
                              {item.status === 'pending' && <button onClick={() => handleItemAction(item.id, 'pending')} disabled={isUpdatingItem} className={cn('px-3 py-2 rounded-lg text-xs font-bold transition-all border', 'bg-amber-500 hover:bg-amber-600 text-white border-amber-600', isUpdatingItem && 'opacity-50 cursor-not-allowed')} title="Marcar en preparación" data-id="element-700">
                                  <Play className="w-4 h-4" data-id="element-701" />
                                </button>}
                              {item.status === 'preparing' && <button onClick={() => handleItemAction(item.id, 'preparing')} disabled={isUpdatingItem} className={cn('px-3 py-2 rounded-lg text-xs font-bold transition-all border', 'bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-600', isUpdatingItem && 'opacity-50 cursor-not-allowed')} title="Marcar listo" data-id="element-702">
                                  <Check className="w-4 h-4" data-id="element-703" />
                                </button>}
                              {isItemReady && <div className="w-10 h-10 rounded-lg bg-emerald-100 border border-emerald-200 flex items-center justify-center" data-id="element-704">
                                  <Check className="w-5 h-5 text-emerald-600" data-id="element-705" />
                                </div>}
                            </div>
                          </li>;
                })}
                    </ul>
                  </div>

                  {/* Order Actions Footer */}
                  <div className={cn('p-4 border-t transition-colors space-y-2', allItemsReady ? 'bg-emerald-50 border-emerald-100' : order.status === 'new' ? 'bg-blue-50 border-blue-100' : 'bg-gray-50 border-gray-100')} data-id="element-706">
                    {/* Global role actions — full order lifecycle */}
                    {isGlobalRole && <>
                        {order.status === 'new' && <Button fullWidth size="lg" onClick={() => handleOrderAction(order.id, 'accept')} isLoading={isUpdatingOrder} className="text-lg font-bold bg-blue-600 hover:bg-blue-700 text-white" data-id="element-707">
                            <Check className="w-5 h-5 mr-2" data-id="element-708" />
                            Aceptar Pedido
                          </Button>}
                        {order.status === 'accepted' && <Button fullWidth size="lg" onClick={() => handleOrderAction(order.id, 'start_preparation')} isLoading={isUpdatingOrder} className="text-lg font-bold bg-amber-500 hover:bg-amber-600 text-white" data-id="element-709">
                            <ChefHat className="w-5 h-5 mr-2" data-id="element-710" />
                            Enviar a Preparación
                          </Button>}
                        {order.status === 'preparing' && <Button fullWidth size="lg" onClick={() => handleOrderAction(order.id, 'mark_ready')} isLoading={isUpdatingOrder} disabled={!allItemsReady} className={cn('text-lg font-bold', allItemsReady ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-200' : 'bg-gray-300 text-gray-500 cursor-not-allowed')} data-id="element-711">
                            <Check className="w-5 h-5 mr-2" data-id="element-712" />
                            {allItemsReady ? 'Marcar Lista ✓' : 'Esperando items...'}
                          </Button>}
                        {order.status === 'ready' && <Button fullWidth size="lg" onClick={() => handleOrderAction(order.id, 'deliver')} isLoading={isUpdatingOrder} className="text-lg font-bold bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-713">
                            <Truck className="w-5 h-5 mr-2" data-id="element-714" />
                            Entregar
                          </Button>}
                        {/* Cancel button — available for new/accepted/preparing */}
                        {['new', 'accepted', 'preparing'].includes(order.status) && <button onClick={() => handleOrderAction(order.id, 'cancel')} disabled={isUpdatingOrder} className="w-full text-center text-sm font-medium text-gray-400 hover:text-red-500 transition-colors py-1" data-id="element-715">
                            <XCircle className="w-3.5 h-3.5 inline mr-1" data-id="element-716" />
                            Cancelar pedido
                          </button>}
                      </>}

                    {/* Station role — only see item-level actions, plus a status indicator */}
                    {!isGlobalRole && <div className="text-center" data-id="element-717">
                        {allItemsReady ? <p className="text-emerald-700 font-bold text-sm" data-id="element-718">
                            ✓ Todos los items listos
                          </p> : <p className="text-gray-500 text-sm" data-id="element-719">
                            Marca cada item cuando esté listo
                          </p>}
                      </div>}
                  </div>
                </div>;
        }) : <div className="col-span-full py-20 flex flex-col items-center justify-center text-gray-500 bg-white rounded-xl border border-gray-200 border-dashed" data-id="element-720">
              <ChefHatIcon className="w-16 h-16 text-gray-300 mb-4" data-id="element-721" />
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-722">
                Sin pedidos activos
              </h3>
              <p data-id="element-723">
                No hay pedidos pendientes para esta estación en este momento.
              </p>
            </div>}
        </div>
      </div>
    </AdminLayout>;
}
function ChefHatIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-724">
      <path d="M17 21a1 1 0 0 0 1-1v-5.35c0-.457.316-.844.727-1.041a4 4 0 0 0-2.134-7.589c-.345 0-.682.041-1.005.115-.428-1.833-2.136-3.134-4.088-3.134s-3.66 1.301-4.088 3.134c-.323-.074-.66-.115-1.005-.115a4 4 0 0 0-2.134 7.589c.411.197.727.584.727 1.041V20a1 1 0 0 0 1 1h12z" data-id="element-725" />
      <path d="M6 17h12" data-id="element-726" />
    </svg>;
}